import { useEffect, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

export function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('inert') && el.getAttribute('aria-hidden') !== 'true',
  );
}

export type UseFocusTrapOptions = {
  /** Element to focus first; defaults to the first focusable element, else the container. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Put focus back where it was when the trap releases (default true). */
  returnFocus?: boolean;
};

/**
 * Keeps keyboard focus inside `container` while `active` (Dialog, Sheet): moves focus in on
 * activation, cycles Tab and Shift+Tab at the edges, and restores the previous focus on release.
 */
export function useFocusTrap(
  container: RefObject<HTMLElement | null>,
  active: boolean,
  options: UseFocusTrapOptions = {},
) {
  const { initialFocus, returnFocus = true } = options;

  useEffect(() => {
    const root = container.current;
    if (!active || !root) return undefined;
    const previous = document.activeElement as HTMLElement | null;

    const first = initialFocus?.current ?? focusableWithin(root)[0];
    if (first) first.focus();
    else {
      if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');
      root.focus();
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusableWithin(root);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const head = items[0];
      const tail = items[items.length - 1];
      if (e.shiftKey && document.activeElement === head) {
        e.preventDefault();
        tail.focus();
      } else if (!e.shiftKey && document.activeElement === tail) {
        e.preventDefault();
        head.focus();
      }
    };
    root.addEventListener('keydown', onKey);
    return () => {
      root.removeEventListener('keydown', onKey);
      if (returnFocus && previous && typeof previous.focus === 'function') previous.focus();
    };
  }, [active, container, initialFocus, returnFocus]);
}
